import axios from 'axios';
import { useState } from 'react';
import { DetectedDocumentActionMode } from '../../../interfaces/document';
import referralService from '../../../services/referral';

interface DetectedDocumentActionDialogState {
  open: boolean;
  mode: DetectedDocumentActionMode | null;
  documentId: number | null;
  detectedDocumentId: number | null;
  patientDocumentId: number | null;
  patientIdentifier: string | null;
  currentType: string | null;
  newType: string;
  reason: string;
  crmNote: string;
  loading: boolean;
}

const INITIAL_STATE: DetectedDocumentActionDialogState = {
  open: false,
  mode: null,
  documentId: null,
  detectedDocumentId: null,
  patientDocumentId: null,
  patientIdentifier: null,
  currentType: null,
  newType: '',
  reason: '',
  crmNote: '',
  loading: false,
};

export const useDetectedDocumentActions = (onSuccess?: () => void) => {
  const [dialogState, setDialogState] =
    useState<DetectedDocumentActionDialogState>(INITIAL_STATE);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const openChangeType = (params: {
    documentId: number;
    detectedDocumentId: number;
    currentType: string;
  }) => {
    setDialogState({
      ...INITIAL_STATE,
      open: true,
      mode: 'change-type',
      documentId: params.documentId,
      detectedDocumentId: params.detectedDocumentId,
      currentType: params.currentType,
    });
  };

  const openDiscard = (params: {
    documentId: number;
    detectedDocumentId: number;
    currentType: string;
  }) => {
    setDialogState({
      ...INITIAL_STATE,
      open: true,
      mode: 'discard',
      documentId: params.documentId,
      detectedDocumentId: params.detectedDocumentId,
      currentType: params.currentType,
    });
  };

  const openPatientDiscard = (params: {
    documentId: number;
    detectedDocumentId: number;
    patientDocumentId: number;
    patientIdentifier: string;
  }) => {
    setDialogState({
      ...INITIAL_STATE,
      open: true,
      mode: 'discard-patient',
      documentId: params.documentId,
      detectedDocumentId: params.detectedDocumentId,
      patientDocumentId: params.patientDocumentId,
      patientIdentifier: params.patientIdentifier,
    });
  };

  const closeDialog = () => {
    if (!dialogState.loading) {
      setDialogState(INITIAL_STATE);
    }
  };

  const setReason = (reason: string) =>
    setDialogState((prev) => ({ ...prev, reason }));
  const setCrmNote = (crmNote: string) =>
    setDialogState((prev) => ({ ...prev, crmNote }));
  const setNewType = (newType: string) =>
    setDialogState((prev) => ({ ...prev, newType }));

  const confirm = async () => {
    const {
      mode,
      documentId,
      detectedDocumentId,
      patientDocumentId,
      currentType,
      newType,
      reason,
      crmNote,
    } = dialogState;
    if (!mode || !documentId || !detectedDocumentId) return;
    if (mode === 'discard-patient' && !patientDocumentId) return;
    if (!reason.trim()) {
      setSnackbar({ open: true, message: 'Reason is required.', severity: 'error' });
      return;
    }
    if (mode === 'change-type') {
      if (!newType) {
        setSnackbar({ open: true, message: 'Select a new document type.', severity: 'error' });
        return;
      }
      if (newType === currentType) {
        setSnackbar({
          open: true,
          message: 'New type must differ from the current type.',
          severity: 'error',
        });
        return;
      }
    }

    setDialogState((prev) => ({ ...prev, loading: true }));
    try {
      let message: string;
      if (mode === 'change-type') {
        const result = await referralService.changeDetectedDocumentType(
          documentId,
          detectedDocumentId,
          { newDocumentType: newType, reason: reason.trim(), crmNote: crmNote.trim() || undefined },
        );
        message = result.message;
      } else if (mode === 'discard-patient' && patientDocumentId) {
        const result = await referralService.discardPatientDocument(
          documentId,
          detectedDocumentId,
          patientDocumentId,
          { reason: reason.trim(), crmNote: crmNote.trim() || undefined },
        );
        message = result.message;
      } else {
        const result = await referralService.discardDetectedDocument(
          documentId,
          detectedDocumentId,
          { reason: reason.trim(), crmNote: crmNote.trim() || undefined },
        );
        message = result.message;
      }
      setSnackbar({ open: true, message, severity: 'success' });
      setDialogState(INITIAL_STATE);
      onSuccess?.();
    } catch (err) {
      const apiMessage =
        axios.isAxiosError(err) && (err.response?.data as any)?.message
          ? (err.response?.data as any).message
          : (err as Error).message || 'Action failed';
      setSnackbar({ open: true, message: apiMessage, severity: 'error' });
      setDialogState((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return {
    dialogState,
    snackbar,
    openChangeType,
    openDiscard,
    openPatientDiscard,
    closeDialog,
    setReason,
    setCrmNote,
    setNewType,
    confirm,
    closeSnackbar,
  };
};
